import { defineStore } from 'pinia'
import {
  isEmptyObject,
  setQueryParam,
  deleteQueryParam,
  getFormattedPrice,
} from 'www/utils'
import {
  getDoctorData,
  getDoctorsData,
  getSelectedDoctor,
} from 'www/vue_components/common/AppointmentPage/interfaces'
import { getDoctorDescription, getHasSuitableAgeInWorkplace } from 'www/vue_components/common/AppointmentPage/functions'
import { apiScheduleTelemed } from 'www/api'
import { APPOINTMENT_TYPES } from 'www/constants'
/* eslint-disable import/no-cycle */
import useClubDataStore from 'www/vue_components/common/AppointmentPage/stores/useClubDataStore'
import useAppointmentDataStore from 'www/vue_components/common/AppointmentPage/stores/useAppointmentDataStore'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
/* eslint-enable import/no-cycle */

const useDoctorDataStore = defineStore('doctorDataStore', {
  state: () => ({
    selectedDoctorSpecialityId: null,
    doctorData: getDoctorData(),
    doctorsData: getDoctorsData(),
    selectedDoctor: getSelectedDoctor(),
    appointmentSpecialitiesRef: null,
    isErrorStateSpeciality: false,
  }),
  getters: {
    selectedWorkplace() {
      const { appointmentObjectTypes } = useAppointmentDataStore()

      if (appointmentObjectTypes.isServices) {
        return {}
      }

      const workplace = this.doctorData.workplaces.find(
        ({ speciality }) => speciality.id === this.selectedDoctorSpecialityId,
      ) || {}

      workplace.hasSuitableAge = true

      const { selectedPatient } = useFamilyProfilesStore()
      const { ageNumber: ageNumberPatient } = selectedPatient

      workplace.hasSuitableAge = getHasSuitableAgeInWorkplace({
        workplace,
        ageNumberPatient,
      })

      return workplace
    },
    isWorkplaceSelected() {
      return !!this.selectedWorkplace.priceList
    },
    scheduleType() {
      return apiScheduleTelemed.getScheduleTypeV2({
        telemed: this.selectedWorkplace.priceList?.priceOnline,
        telemedMedtochka: this.selectedWorkplace.priceList?.priceMedtochkaTelemed,
      })
    },
    rangeAgeLimit() {
      const { appointmentObjectTypes } = useAppointmentDataStore()

      if (appointmentObjectTypes.isServices) {
        return {}
      }

      if (this.selectedDoctorSpecialityId && this.doctorData.workplaces.length) {
        return this.selectedWorkplace.age
      }

      return {}
    },
    rangeAgeLimitAllSpecialities() {
      const { appointmentObjectTypes } = useAppointmentDataStore()

      if (appointmentObjectTypes.isDoctor) {
        return this.doctorData.workplaces.map(({ age }) => age || {})
      }

      return []
    },
    getFullPriceForWorkplace: () => workplace => {
      if (!workplace.priceList) {
        return ''
      }

      const appointmentDataStore = useAppointmentDataStore()

      let fullPrice
      const {
        price, priceHome, priceOnline, priceMedtochkaTelemed,
      } = workplace.priceList
      const {
        isLpu, isHome, isOnline, isTelemed, isClub, isTelemedMedtochka,
      } = appointmentDataStore.appointmentPlaceTypes

      if (isLpu || isClub) {
        fullPrice = price
      } else if (isHome) {
        fullPrice = priceHome
      } else if (isOnline || isTelemed) {
        fullPrice = priceOnline
      } else if (isTelemedMedtochka) {
        fullPrice = priceMedtochkaTelemed
      }

      return getFormattedPrice(fullPrice)
    },
    workplacePriceText() {
      if (!this.selectedWorkplace.priceList) {
        return ''
      }

      const appointmentDataStore = useAppointmentDataStore()

      let price = null

      if (appointmentDataStore.appointmentPlaceTypes.isLpu || appointmentDataStore.appointmentPlaceTypes.isClub) {
        price = this.selectedWorkplace.priceList.price
      } else if (appointmentDataStore.appointmentPlaceTypes.isHome) {
        price = this.selectedWorkplace.priceList.priceHome
      } else if (
        appointmentDataStore.appointmentPlaceTypes.isOnline
        || appointmentDataStore.appointmentPlaceTypes.isTelemed
        || appointmentDataStore.appointmentPlaceTypes.isTelemedMedtochka
      ) {
        price = this.selectedWorkplace.priceList.priceOnline ?? this.selectedWorkplace.priceList.priceMedtochkaTelemed
      }

      if (price === null) {
        return ''
      }

      return getFormattedPrice(price)
    },
    getClubPriceForWorkplace: () => ({ priceList }) => (
      priceList
        ? getFormattedPrice(priceList.priceClub)
        : ''
    ),
    fullPriceForSelectedWorkplace() {
      return isEmptyObject(this.selectedWorkplace)
        ? null
        : this.getFullPriceForWorkplace(this.selectedWorkplace)
    },
    clubPriceForSelectedWorkplace() {
      return isEmptyObject(this.selectedWorkplace)
        ? null
        : this.getClubPriceForWorkplace(this.selectedWorkplace)
    },
    doctorSubtitle() {
      return getDoctorDescription(this.doctorData)
    },
    isSomeWorkplaceInClub() {
      return this.doctorData.workplaces.some(({ isInClub }) => isInClub)
    },
    radioGroupValue() {
      return this.selectedWorkplace.hasSuitableAge ? this.selectedDoctorSpecialityId : null
    },
    isTelemedAvailable() {
      return typeof this.selectedWorkplace.priceList?.priceOnline === 'number'
    },
    isTelemedMedtochkaAvailable() {
      return typeof this.selectedWorkplace.priceList?.priceMedtochkaTelemed === 'number'
    },
    hasDifferentTelemedPrices() {
      return typeof this.selectedWorkplace.priceList?.priceOnline === 'number'
        && typeof this.selectedWorkplace.priceList?.priceMedtochkaTelemed === 'number'
        && this.selectedWorkplace.priceList.priceOnline !== this.selectedWorkplace.priceList.priceMedtochkaTelemed
    },
  },
  actions: {
    updateErrorStateSpeciality(payload) {
      this.isErrorStateSpeciality = payload
    },
    updateAppointmentSpecialitiesRef(payload) {
      this.appointmentSpecialitiesRef = payload
    },
    updateStateSelectedDoctorSpecialityId(payload) {
      this.selectedDoctorSpecialityId = payload
    },
    updateSelectedDoctor(payload) {
      this.selectedDoctor = payload
    },
    resetSelectedDoctor() {
      this.selectedDoctor = getSelectedDoctor()
    },
    validateSelectedSpeciality() {
      this.updateErrorStateSpeciality(!this.radioGroupValue)

      if (!this.radioGroupValue) {
        return this.appointmentSpecialitiesRef
      }
    },
    setStateSelectedDoctorSpecialityId(specId) {
      const clubDataStore = useClubDataStore()
      const appointmentDataStore = useAppointmentDataStore()

      this.updateStateSelectedDoctorSpecialityId(specId)

      setQueryParam('speciality', specId)

      const {
        isLpu,
        isClub,
      } = appointmentDataStore.appointmentPlaceTypes

      if (!isLpu && !isClub) {
        return
      }

      const isClubSelectedWorkplace = this.selectedWorkplace.isInClub && clubDataStore.isClubPaymentTypeSelected

      if (isClubSelectedWorkplace) {
        appointmentDataStore.updateInitAdditionalPlaceType(APPOINTMENT_TYPES.club)
        setQueryParam('type', APPOINTMENT_TYPES.club)
      } else {
        appointmentDataStore.updateInitAdditionalPlaceType(APPOINTMENT_TYPES.lpu)
        deleteQueryParam('type')
      }
    },
  },
})

export default useDoctorDataStore
